import { ReviewStars, type ReviewStarValue } from '@/app/_common/review-stars'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { CharacterCount, Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'
import { useSaveContactMutation } from '@/hooks/api/use-save-contact-mutation'
import { trimAndValidate } from '@/lib/utils'
import { T, type TranslationKey, useTranslate } from '@tolgee/react'
import createDecorator from 'final-form-focus'
import { Field, Form } from 'react-final-form'

type ContactFormProps = Readonly<{
  onSubmitSuccess?: () => void
  contactType: ContactFormType
  renderFooter: (submitButton: React.ReactNode) => React.ReactNode
}>

function ContactForm({ onSubmitSuccess, contactType, renderFooter }: ContactFormProps) {
  const { t } = useTranslate()
  const { mutate, isPending } = useSaveContactMutation({
    onSuccess: () => onSubmitSuccess && onSubmitSuccess(),
    onError: (e) => {
      console.error(e)
      toast.error(t('account.contact.toasts.error'))
    },
  })

  return (
    <Form<ContactFormData>
      onSubmit={(initialValues) => trimAndValidate({ initialValues, validate, onValid: mutate })}
      initialValues={{ contact_type: contactType, comment: '' }}
      decorators={[focusOnErrors]}
      validate={validate}
    >
      {({ handleSubmit, invalid }) => {
        const displayContent = FIELD_DATA[contactType]
        return (
          <form onSubmit={handleSubmit} className='flex flex-col gap-6'>
            <div className='flex flex-col gap-4'>
              {displayContent?.showFeedback && (
                <Field<ReviewStarValue> name='rating'>
                  {({ input, meta }) => (
                    <div className='flex flex-wrap items-center justify-between gap-4'>
                      <Label
                        htmlFor={input.name}
                        error={meta.submitFailed && meta.invalid}
                        variant='dialog-field'
                      >
                        <T keyName={displayContent.labelKey} />
                      </Label>
                      <ReviewStars {...input} disabled={isPending} selectedValue={input.value} />
                    </div>
                  )}
                </Field>
              )}
              <Field<string> name='comment'>
                {({ input, meta }) => (
                  <div className='flex flex-col gap-4 space-y-0'>
                    {!displayContent?.showFeedback && (
                      <Label
                        htmlFor={input.name}
                        error={meta.submitFailed && meta.invalid}
                        variant='dialog-field'
                      >
                        <T keyName={displayContent.labelKey} />
                      </Label>
                    )}
                    <div className='flex flex-col gap-2'>
                      <Input
                        disabled={isPending}
                        error={meta.submitFailed && meta.invalid}
                        placeholder={t(displayContent.placeholderKey)}
                        maxLength={MAX_COMMENT_CHARS}
                        {...input}
                      />
                      <CharacterCount length={input.value.length} max={MAX_COMMENT_CHARS} />
                    </div>
                  </div>
                )}
              </Field>
            </div>
            {renderFooter(
              <Button
                type='submit'
                title={t('account.contact.dialog.button.a11y')}
                loading={isPending}
                disabled={invalid}
              >
                <T keyName='account.contact.dialog.button.title' />
              </Button>,
            )}
          </form>
        )
      }}
    </Form>
  )
}

// 👇 types

type ContactFormType = (typeof CONTACT_FORM_TYPES)[number]

type ContactFormData = {
  contact_type: ContactFormType
  comment: string
  rating?: ReviewStarValue
}
type ContactFormErrors = Partial<Record<keyof ContactFormData, boolean>>

// 👇 form

const focusOnErrors = createDecorator<ContactFormData>()
const validate = ({ contact_type, rating, comment }: ContactFormData): ContactFormErrors => {
  const errors: ContactFormErrors = {}

  if (!CONTACT_FORM_TYPES.includes(contact_type)) errors.contact_type = true
  if (contact_type === 'FEEDBACK' && !rating) errors.rating = true
  if (!comment?.trim() || comment.length === 0) errors.comment = true

  return errors
}

// 👇 static data

const CONTACT_FORM_TYPES = ['HELP', 'FEEDBACK', 'BUGS'] as const

const MAX_COMMENT_CHARS = 1000

const FIELD_DATA: Record<
  ContactFormType,
  {
    labelKey: TranslationKey
    placeholderKey: TranslationKey
    valueKey: TranslationKey
    showFeedback?: boolean
  }
> = {
  HELP: {
    labelKey: 'account.contact.dialog.typeOptions.help.label',
    placeholderKey: 'account.contact.dialog.typeOptions.help.placeholder',
    valueKey: 'account.contact.dialog.typeOptions.help.value',
  },
  FEEDBACK: {
    labelKey: 'account.contact.dialog.typeOptions.feedback.label',
    placeholderKey: 'account.contact.dialog.typeOptions.feedback.placeholder',
    valueKey: 'account.contact.dialog.typeOptions.feedback.value',
    showFeedback: true,
  },
  BUGS: {
    labelKey: 'account.contact.dialog.typeOptions.bugs.label',
    placeholderKey: 'account.contact.dialog.typeOptions.bugs.placeholder',
    valueKey: 'account.contact.dialog.typeOptions.bugs.value',
  },
}

export { CONTACT_FORM_TYPES, ContactForm, type ContactFormData, type ContactFormType }
